/**
 * MSAL.js configuración para login OIDC contra Microsoft Entra ID.
 *
 * Los valores se inyectan en build time via Vite env vars:
 *   VITE_ENTRA_TENANT_ID  — tenant ID del directorio Entra
 *   VITE_ENTRA_CLIENT_ID  — application ID de la app OIDC registrada (reusa la del portal)
 *
 * Recordar: agregar https://etrm.mppenergia.com como Redirect URI en la app
 * registration de Entra antes de probar prod.
 */
import {
  BrowserAuthError,
  BrowserAuthErrorCodes,
  type Configuration,
  EventType,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from "@azure/msal-browser";

// Fallas del iframe oculto con el que MSAL intenta renovar la sesión en
// silencio cuando el refresh token ya no sirve (sesión de Entra vencida,
// cookies de terceros bloqueadas por el navegador, proxy de la oficina…). Con
// estas el único camino es el redirect interactivo, igual que con
// InteractionRequiredAuthError; si no se atrapan, la página muestra
// "monitor_window_timeout: Token acquisition in iframe failed due to timeout".
const ERRORES_IFRAME = new Set<string>([
  BrowserAuthErrorCodes.monitorWindowTimeout,
  BrowserAuthErrorCodes.hashEmptyError,
  BrowserAuthErrorCodes.hashDoesNotContainKnownProperties,
  BrowserAuthErrorCodes.iframeClosedPrematurely,
  BrowserAuthErrorCodes.blockIframeReload,
]);

const TENANT_ID = import.meta.env.VITE_ENTRA_TENANT_ID || "";
const CLIENT_ID = import.meta.env.VITE_ENTRA_CLIENT_ID || "";

/**
 * Si `VITE_DEV_BYPASS_AUTH=true`, MSAL no se inicializa y la app asume
 * un usuario fake (`VITE_DEV_USER_EMAIL`). El backend debe correr con
 * `ENV=dev` y `DEV_USER_EMAIL=...` para aceptar requests sin Bearer token.
 */
export const DEV_BYPASS_AUTH =
  import.meta.env.VITE_DEV_BYPASS_AUTH === "true";

export const DEV_USER_EMAIL =
  import.meta.env.VITE_DEV_USER_EMAIL || "dev@local";

export const msalConfig: Configuration = {
  auth: {
    clientId: CLIENT_ID,
    authority: TENANT_ID
      ? `https://login.microsoftonline.com/${TENANT_ID}`
      : "https://login.microsoftonline.com/common",
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    // localStorage: la sesión sobrevive pestañas nuevas y reinicios del
    // navegador (con sessionStorage cada pestaña obligaba a pasar por login).
    cacheLocation: "localStorage",
    storeAuthStateInCookie: false,
  },
};

export const loginRequest = {
  scopes: ["openid", "profile", "email", "User.Read"],
};

export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * SSO entre los sitios de MPP (home, etrm, portal-edocta, organizacion,
 * escribano). Cada sitio es otro origen y no ve la caché de MSAL de los demás,
 * pero todos usan la misma app de Entra: si el navegador ya tiene sesión de
 * Microsoft, `ssoSilent` la reutiliza sin mostrar nada.
 * Si el navegador bloquea ese iframe (cookies de terceros) y se llegó desde una
 * liga del home (`?sso=1`), se hace un redirect completo: con la sesión de
 * Microsoft viva, Entra regresa solo, sin pedir credenciales. El home manda
 * además `login_hint` para que Entra no pregunte qué cuenta usar.
 */
async function intentarSso(): Promise<void> {
  if (window.self !== window.top) return; // somos el iframe del propio ssoSilent
  const url = new URL(window.location.href);
  const desdeHome = url.searchParams.get("sso") === "1";
  const loginHint = url.searchParams.get("login_hint") || undefined;
  if (url.searchParams.has("sso") || url.searchParams.has("login_hint")) {
    url.searchParams.delete("sso");
    url.searchParams.delete("login_hint");
    window.history.replaceState(window.history.state, "", url.toString());
  }
  if (msalInstance.getAllAccounts().length) return;
  try {
    const r = await msalInstance.ssoSilent({ ...loginRequest, loginHint });
    msalInstance.setActiveAccount(r.account);
  } catch {
    if (desdeHome) {
      // Regresa a la misma página (ya sin ?sso, así que no hay ciclo).
      await msalInstance.loginRedirect({ ...loginRequest, loginHint, redirectStartPage: url.toString() });
    }
  }
}

// Inicializar la instancia (MSAL v3+ requiere initialize() antes de usar).
export async function initMsal(): Promise<void> {
  await msalInstance.initialize();
  await msalInstance.handleRedirectPromise();

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length && !msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  await intentarSso();

  msalInstance.addEventCallback((event) => {
    if (
      event.eventType === EventType.LOGIN_SUCCESS &&
      event.payload &&
      "account" in event.payload &&
      event.payload.account
    ) {
      msalInstance.setActiveAccount(event.payload.account);
    }
  });
}

// Guarda anti-loop del redirect de renovación: si un 401 persistente tiene
// otra causa (reloj desfasado, backend rechazando un token válido…), sin esta
// guarda la app entraría en un ciclo infinito de recargas contra Entra.
const RENOVAR_TS_KEY = "etrm-renovar-sesion-ts";

/**
 * Redirige a Entra para renovar la sesión cuando la renovación silenciosa ya
 * no es posible (refresh token de SPA vencido a las ~24 h, o revocado) o el
 * backend regresó 401. Si la sesión de Microsoft sigue viva, Entra regresa
 * solo a la app sin pedir credenciales. No-op en dev-bypass; como máximo un
 * redirect por minuto (anti-loop).
 */
export function renovarSesion(): void {
  if (DEV_BYPASS_AUTH) return;
  try {
    const ultimo = Number(sessionStorage.getItem(RENOVAR_TS_KEY) || 0);
    if (Date.now() - ultimo < 60_000) return;
    sessionStorage.setItem(RENOVAR_TS_KEY, String(Date.now()));
  } catch {
    // storage bloqueado: se intenta el redirect de todas formas
  }
  const account = msalInstance.getActiveAccount() ?? undefined;
  void msalInstance
    .acquireTokenRedirect({ ...loginRequest, account })
    .catch(() => {
      // p. ej. otra interacción MSAL en curso: no hay nada más que hacer aquí
    });
}

/** exp (epoch segundos) del payload de un JWT, o null si no se puede leer. */
function jwtExp(token: string): number | null {
  try {
    const payload = JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
    );
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

/**
 * Obtiene un id_token fresco (refresca silenciosamente si expiró).
 * Tira si no hay sesión activa — el llamador debe forzar login.
 *
 * OJO: acquireTokenSilent decide si renueva según la vigencia del ACCESS
 * token, no del id_token; con sesiones largas puede devolver un id_token
 * cacheado YA EXPIRADO (el backend lo rechaza con "Token expirado"). Por eso
 * checamos el exp del id_token y forzamos el refresh si vence en <5 min.
 */
export async function acquireIdToken(): Promise<string> {
  const account = msalInstance.getActiveAccount();
  if (!account) {
    // Sin cuenta (storage limpiado, sesión de otra pestaña cerrada…): al login.
    renovarSesion();
    throw new Error("Sin sesión activa — redirigiendo al inicio de sesión…");
  }
  try {
    let result = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account,
    });
    const exp = jwtExp(result.idToken);
    if (exp !== null && exp * 1000 < Date.now() + 5 * 60_000) {
      result = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account,
        forceRefresh: true,
      });
    }
    return result.idToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      // El refresh token de SPA ya no renueva en silencio (límite ~24 h de
      // Entra, o sesión revocada): redirect al login — regresa solo si la
      // sesión de Microsoft sigue viva.
      renovarSesion();
      throw new Error(
        "Tu sesión de Microsoft 365 expiró — redirigiendo al inicio de sesión…",
      );
    }
    if (e instanceof BrowserAuthError && ERRORES_IFRAME.has(e.errorCode)) {
      // El intento silencioso por iframe no regresó (timeout / sin hash):
      // mismo remedio, redirect interactivo.
      renovarSesion();
      throw new Error(
        "No se pudo renovar la sesión en segundo plano — redirigiendo al inicio de sesión…",
      );
    }
    throw e;
  }
}

export function currentEmail(): string | null {
  if (DEV_BYPASS_AUTH) return DEV_USER_EMAIL;
  return msalInstance.getActiveAccount()?.username ?? null;
}
